import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";

const STATUSES = ["new", "contacted", "joined", "closed"];
const TYPE_LABEL = { membership: "Membership", guest_pass: "Guest pass", contact: "Contact" };

export function LeadsTable() {
  const [leads, setLeads] = useState(null);
  const load = () => http.get("/admin/leads").then((r) => setLeads(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    await http.patch(`/admin/leads/${id}`, { status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await http.delete(`/admin/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
  };

  if (!leads) return <p className="text-sm text-white/50">Loading leads…</p>;
  return (
    <section className="rounded-md border border-white/10 bg-[#111] p-4 sm:p-6" data-testid="admin-leads">
      <h2 className="h-display text-3xl text-white">Leads <span className="text-[var(--gold)]">({leads.length})</span></h2>
      <p className="mb-4 text-sm text-white/50">Every form submission is saved here and emailed to the gym inbox.</p>
      {leads.length === 0 ? <p className="text-sm text-white/50" data-testid="admin-leads-empty">No leads yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead><tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-white/45">
              <th className="p-2">Date</th><th className="p-2">Type</th><th className="p-2">Contact</th><th className="p-2">Details</th><th className="p-2">Status</th><th className="p-2" />
            </tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-white/5 align-top" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="p-2 text-white/60">{new Date(l.created_at).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="p-2"><span className="rounded-sm bg-[var(--gold)]/15 px-2 py-0.5 text-xs text-[var(--gold)]">{TYPE_LABEL[l.type]}</span></td>
                  <td className="p-2 text-white"><p className="font-semibold">{l.name}</p><a href={`mailto:${l.email}`} className="text-white/60 hover:underline">{l.email}</a>{l.phone && <a href={`tel:${l.phone}`} className="block text-white/60">{l.phone}</a>}</td>
                  <td className="p-2 text-white/70">
                    {[l.location && `Gym: ${l.location}`, l.goal && `Goal: ${l.goal}`, l.plan && `Plan: ${l.plan}`].filter(Boolean).join(" · ")}
                    {l.message && <p className="mt-1 text-white/55">{l.message}</p>}
                    {l.email_status === "failed" && <p className="mt-1 text-xs text-red-400">Email notification failed</p>}
                  </td>
                  <td className="p-2">
                    <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className="rounded-sm border border-white/15 bg-black/40 px-2 py-1 text-xs" data-testid={`admin-lead-status-${l.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-2"><button onClick={() => remove(l.id)} className="text-red-400/70 hover:text-red-400" aria-label="Delete lead" data-testid={`admin-lead-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
